import {
  CARELON_PROVIDER_DIRECTORY_PLACEMENTS,
  CARELON_PROVIDER_DIRECTORY_TREATMENT_GROUPS,
  EMPTY_CARELON_PROVIDER_DIRECTORY_FEATURE_FLAGS,
} from './constants'
import {
  carelonDirectoryCustomerConfigurations,
  getCarelonDirectoryCustomerConfiguration,
} from './metadata/configuration'
import { assertUnreachable } from '../common/utils/commonUtils'
import { ZeroResultsEnhancementType } from '../providers/constants'
import {
  isCarelonOutpatientTherapyTreatmentOption,
  isSelfInitiatedTeenSearchingForCare,
  isTreatmentOptionMedicationManagement,
} from '../searchForCare/utils'

import type {
  CarelonDirectoryContext,
  CarelonDirectoryCustomerConfiguration,
  CarelonDirectoryCustomerConfigurations,
  CarelonDirectoryDecision,
  CarelonDirectoryEligibilityRule,
  CarelonDirectoryPlacementContext,
  CarelonDirectoryPlacementParams,
  CarelonDirectoryPlacementProps,
} from './types'
import type { TREATMENT_OPTIONS_TYPE } from '../common/constants/treatmentOptions'

const treatmentGroupMatchers: Record<
  CARELON_PROVIDER_DIRECTORY_TREATMENT_GROUPS,
  (treatmentOption: TREATMENT_OPTIONS_TYPE | null) => boolean
> = {
  [CARELON_PROVIDER_DIRECTORY_TREATMENT_GROUPS.OUTPATIENT_THERAPY]: isCarelonOutpatientTherapyTreatmentOption,
  [CARELON_PROVIDER_DIRECTORY_TREATMENT_GROUPS.MEDICATION_MANAGEMENT]: isTreatmentOptionMedicationManagement,
}

/**
 * Selects the customer's rules for the current page. The legacy Care Navigator page must be
 * reached through zero results; enhanced pages also filter by layout. Recommendation tier
 * is checked separately because the layout does not tell us the member's acuity.
 */
const getPlacementRules = (
  configuration: CarelonDirectoryCustomerConfiguration,
  placement: CarelonDirectoryPlacementContext,
): readonly CarelonDirectoryEligibilityRule[] => {
  let rules: readonly CarelonDirectoryEligibilityRule[] | undefined

  switch (placement.type) {
    case CARELON_PROVIDER_DIRECTORY_PLACEMENTS.PROVIDER_RESULTS:
      rules = configuration.placements[placement.type]
      break
    case CARELON_PROVIDER_DIRECTORY_PLACEMENTS.CONSOLIDATED_CARE_NAVIGATOR:
      if (!placement.isZeroProvidersFlow) {
        return []
      }
      rules = configuration.placements[placement.type]
      break
    case CARELON_PROVIDER_DIRECTORY_PLACEMENTS.ENHANCED_ZERO_RESULTS: {
      const { enhancementType } = placement
      if (enhancementType === ZeroResultsEnhancementType.NONE) {
        return []
      }
      rules = configuration.placements[placement.type]?.filter((rule) =>
        rule.enhancementTypes.includes(enhancementType),
      )
      break
    }
    default:
      assertUnreachable(placement)
  }

  return rules ?? []
}

/** Checks the treatment, required flag, recommendation tier, and teen restriction for one rule. */
const getIsRuleEligible = (rule: CarelonDirectoryEligibilityRule, context: CarelonDirectoryContext): boolean => {
  const matchesTreatment = treatmentGroupMatchers[rule.treatmentGroup](context.selectedTreatmentOption)
  const featureFlags = context.featureFlags ?? EMPTY_CARELON_PROVIDER_DIRECTORY_FEATURE_FLAGS
  const hasRequiredFeatureFlag =
    rule.requiredFeatureFlag === undefined || featureFlags[rule.requiredFeatureFlag] === true

  const { recommendationTierScope } = rule
  const matchesRecommendationTier =
    recommendationTierScope.type === 'unrestricted' ||
    (context.recommendationTier !== undefined && recommendationTierScope.tiers.includes(context.recommendationTier))
  const isSearcherAllowed = rule.searcherPolicy === 'all' || context.isSelfInitiatedTeenSearchingForCare === false

  return matchesTreatment && hasRequiredFeatureFlag && matchesRecommendationTier && isSearcherAllowed
}

/**
 * Main eligibility check for a Carelon placement. Looks up the customer's configuration,
 * requires a nonblank URL, and checks whether any rule for the current page matches the search.
 * Returns the URL only when the placement is available, so screens and analytics share the same decision logic.
 */
export const resolveCarelonDirectoryPlacement = (
  context: CarelonDirectoryContext,
  configurations: CarelonDirectoryCustomerConfigurations = carelonDirectoryCustomerConfigurations,
): CarelonDirectoryDecision => {
  const configuration = getCarelonDirectoryCustomerConfiguration(context.customerName, configurations)
  if (!configuration?.directoryUrl.trim()) {
    return { status: 'unavailable' }
  }

  const isAvailable = getPlacementRules(configuration, context.placement).some((rule) =>
    getIsRuleEligible(rule, context),
  )

  return isAvailable ? { status: 'available', url: configuration.directoryUrl } : { status: 'unavailable' }
}

/** Checks who the search is for and returns the visibility and URL used by Carelon links. */
export const getCarelonDirectoryPlacementProps = (
  { isSelfCarelonOutpatientTherapySearch, searcherDOB, ...context }: CarelonDirectoryPlacementParams,
  configurations?: CarelonDirectoryCustomerConfigurations,
): CarelonDirectoryPlacementProps => {
  const decision = resolveCarelonDirectoryPlacement(
    {
      ...context,
      isSelfInitiatedTeenSearchingForCare: isSelfInitiatedTeenSearchingForCare({
        isSearchingForAdultTherapy: isSelfCarelonOutpatientTherapySearch,
        searcherDOB: searcherDOB ?? '',
      }),
    },
    configurations,
  )

  if (decision.status === 'unavailable') {
    return { shouldShowCarelonDirectory: false }
  }

  return { shouldShowCarelonDirectory: true, carelonProviderDirectoryUrl: decision.url }
}
